"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { detectPlatform, detectStandalone, type PwaPlatform } from "@/lib/pwa-platform";

export type { PwaPlatform };

interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

/**
 * Maneja el flujo de instalación de la PWA: captura `beforeinstallprompt` (Android/Chrome/desktop),
 * detecta iOS (donde no existe ese evento) y detecta si la app ya corre instalada (standalone).
 */
export function usePwaInstall() {
  const [platform, setPlatform] = useState<PwaPlatform>("other");
  const [isStandalone, setIsStandalone] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canPrompt, setCanPrompt] = useState(false);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    setPlatform(detectPlatform());
    setIsStandalone(detectStandalone());

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setCanPrompt(true);
    };
    const onAppInstalled = () => {
      deferredPrompt.current = null;
      setCanPrompt(false);
      setIsInstalled(true);
    };
    const mq = window.matchMedia("(display-mode: standalone)");
    const onDisplayModeChange = () => setIsStandalone(detectStandalone());

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    mq.addEventListener("change", onDisplayModeChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
      mq.removeEventListener("change", onDisplayModeChange);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
    const evt = deferredPrompt.current;
    if (!evt) return "unavailable";
    await evt.prompt();
    const choice = await evt.userChoice;
    deferredPrompt.current = null;
    setCanPrompt(false);
    if (choice.outcome === "accepted") setIsInstalled(true);
    return choice.outcome;
  }, []);

  return {
    platform,
    /** true si la app corre en modo standalone o si el navegador confirmó la instalación */
    isInstalled: isInstalled || isStandalone,
    /** true si hay un prompt nativo de instalación disponible (Android/Chrome/desktop) */
    canPrompt,
    promptInstall,
  };
}
